import React, { useState, useCallback } from 'react';
import { Card, Image, Dropdown, Badge, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';

import TruncatedTextTooltip from '@/components/common/TruncatedTextTooltip';
import { buildPostPermalink } from '@/shared/utils/shareUrls';

import { ReplySection } from '../replies';

import PostActions from './PostActions';
import '../replies/Replies.css';

const POST_CONTENT_TRUNCATE_LENGTH = 300;

const RESOURCE_TYPE_LABELS = {
    article: 'Article',
    dataset: 'Dataset',
    tool: 'Tool',
    report: 'Report',
};

const isSafeUrl = (url) => {
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

const TOMBSTONE_LABELS = {
    author: '[deleted by author]',
    moderator: '[deleted by moderator]',
    admin: '[deleted by site admin]',
};

const PostCard = ({ post, engagement, handlers, permissions }) => {
    const {
        postId,
        authorUsername,
        communityName,
        postType = 'Discussion Post',
        postIcon,
        title,
        userName,
        userProfilePic,
        userDate,
        userFullDate,
        content,
        contentHighlight,
        mediaUrl,
        titleAsLink = true,
        resourceUrl,
        resourceType,
        eventDate,
        eventEndDate,
        eventLocation,
        eventRegistrationUrl,
        flashMessage = null,
        likes = 0,
        dislikes = 0,
        comments = 0,
        shares = 0,
        isPinned = false,
        slug,
        status,
        deletedByRole,
    } = post;

    const { reaction = null, isBookmarked = false } = engagement;
    const {
        onLike,
        onDislike,
        onComment,
        onBookmark,
        onReport,
        onDelete,
        onPin,
        onDismissFlash,
    } = handlers;
    const { isModerator = false, canDelete = false, user = null } = permissions;

    const [isContentExpanded, setIsContentExpanded] = useState(false);
    const [showReplies, setShowReplies] = useState(false);
    const [localCommentCount, setLocalCommentCount] = useState(
        Math.max(0, comments)
    );

    const handleToggleReplies = () => {
        setShowReplies((prev) => !prev);
        if (onComment) {
            onComment();
        }
    };

    const handleReplyCountChange = useCallback((count) => {
        setLocalCommentCount(Math.max(0, count));
    }, []);

    const isLongContent =
        typeof content === 'string' &&
        content.length > POST_CONTENT_TRUNCATE_LENGTH;
    const isContentCollapsed = isLongContent && !isContentExpanded;

    // Build permalink URL for social sharing
    const shareUrl = buildPostPermalink(slug, postId);
    const authorDisplayName = userName || 'User';

    // Tombstone view: the post has been soft-deleted, so its title/content
    // were cleared server-side and we render a placeholder in their place.
    // Replies under a deleted post stay live and remain interactive, so the
    // comments toggle + ReplySection are kept available below.
    if (status === 'deleted') {
        const placeholder = TOMBSTONE_LABELS[deletedByRole] || '[deleted]';
        return (
            <Card className="post-card mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                    <div className="post-meta d-flex align-items-center mb-3 text-muted small">
                        <span title={userFullDate}>{userDate}</span>
                    </div>
                    <p className="text-muted fst-italic mb-3">{placeholder}</p>
                    {postId && (
                        <>
                            <Button
                                variant="link"
                                className="p-0 text-dark"
                                onClick={handleToggleReplies}
                                aria-expanded={showReplies}
                            >
                                {showReplies
                                    ? `Hide ${localCommentCount} ${localCommentCount === 1 ? 'reply' : 'replies'}`
                                    : `Show ${localCommentCount} ${localCommentCount === 1 ? 'reply' : 'replies'}`}
                            </Button>
                            {showReplies && (
                                <ReplySection
                                    postId={postId}
                                    isModerator={isModerator}
                                    onReplyCountChange={handleReplyCountChange}
                                />
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card
            className={`post-card mb-4 border-0 shadow-sm${isPinned ? ' border-start border-primary border-3' : ''}`}
        >
            <Card.Body className="p-4">
                {/* Pinned indicator */}
                {isPinned && (
                    <div className="d-flex align-items-center gap-1 text-primary small fw-semibold mb-2">
                        <i
                            className="bi bi-pin-angle-fill"
                            aria-hidden="true"
                        />
                        <span>Pinned post</span>
                    </div>
                )}

                {/* Post Header */}
                <div className="post-header d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                        {postIcon && (
                            <Image
                                src={postIcon}
                                alt="post type icon"
                                className="post-type-icon me-2"
                            />
                        )}
                        {postType === 'resource' ? (
                            <>
                                <i
                                    className="bi bi-link-45deg text-primary"
                                    aria-hidden="true"
                                />
                                <span className="post-type-label">
                                    Resource
                                </span>
                                {resourceType && (
                                    <Badge bg="secondary" className="fw-normal">
                                        {RESOURCE_TYPE_LABELS[resourceType] ||
                                            resourceType}
                                    </Badge>
                                )}
                            </>
                        ) : postType === 'event' ? (
                            <>
                                <i
                                    className="bi bi-calendar-event text-primary"
                                    aria-hidden="true"
                                />
                                <span className="post-type-label">Event</span>
                                {eventDate && (
                                    <Badge
                                        bg={
                                            new Date(
                                                eventEndDate || eventDate
                                            ) >= new Date()
                                                ? 'success'
                                                : 'secondary'
                                        }
                                        className="fw-normal"
                                    >
                                        {new Date(eventEndDate || eventDate) >=
                                        new Date()
                                            ? 'Upcoming'
                                            : 'Past'}
                                    </Badge>
                                )}
                            </>
                        ) : (
                            <span className="post-type-label">{postType}</span>
                        )}
                    </div>
                    {user && (
                        <Dropdown align="end">
                            <Dropdown.Toggle
                                variant="link"
                                className="post-settings-toggle text-decoration-none p-0 text-primary"
                                id={`post-settings-${postId}`}
                                aria-label="Post options"
                            >
                                <i
                                    className="bi bi-three-dots"
                                    aria-hidden="true"
                                />
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item
                                    onClick={onReport}
                                    className="d-flex align-items-center text-primary"
                                >
                                    <i className="bi bi-shield-exclamation me-2" />
                                    Report
                                </Dropdown.Item>
                                {isModerator && (
                                    <Dropdown.Item
                                        onClick={onPin}
                                        className="d-flex align-items-center text-primary"
                                    >
                                        <i
                                            className={`bi ${isPinned ? 'bi-pin-angle' : 'bi-pin-angle-fill'} me-2`}
                                        />
                                        {isPinned ? 'Unpin post' : 'Pin post'}
                                    </Dropdown.Item>
                                )}
                                {canDelete && (
                                    <Dropdown.Item
                                        onClick={onDelete}
                                        className="d-flex align-items-center text-danger"
                                    >
                                        <i className="bi bi-trash me-2" />
                                        Delete
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>

                {/* User Info */}
                <div className="post-meta d-flex align-items-center mb-2">
                    {userProfilePic ? (
                        <Image
                            src={userProfilePic}
                            alt={`${authorDisplayName} avatar`}
                            roundedCircle
                            className="post-user-avatar me-2"
                        />
                    ) : (
                        <div
                            className="post-user-avatar-fallback me-2"
                            aria-label={`${authorDisplayName} avatar`}
                        >
                            {authorDisplayName.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div>
                        {authorUsername ? (
                            <Link
                                to={`/users/${authorUsername}`}
                                className="post-user-name fw-bold text-decoration-none text-dark"
                            >
                                {authorDisplayName}
                            </Link>
                        ) : (
                            <span className="post-user-name fw-bold">
                                {authorDisplayName}
                            </span>
                        )}
                        <span
                            className="text-muted ms-2 small"
                            title={userFullDate}
                        >
                            {userDate}
                        </span>
                    </div>
                </div>

                {/* Post Title */}
                {title && (
                    <h5 className="post-title mb-3">
                        {slug && postId && titleAsLink ? (
                            <Link
                                to={`/communities/${slug}/posts/${postId}`}
                                className="text-decoration-none text-dark"
                            >
                                {title}
                            </Link>
                        ) : (
                            title
                        )}
                    </h5>
                )}

                {/* Resource Link Preview */}
                {postType === 'resource' &&
                    resourceUrl &&
                    isSafeUrl(resourceUrl) && (
                        <a
                            href={resourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none d-block mb-3"
                            aria-label={`Open resource: ${resourceUrl}`}
                        >
                            <div className="d-flex align-items-center gap-3 p-3 border rounded bg-light">
                                <i
                                    className="bi bi-box-arrow-up-right fs-5 text-primary flex-shrink-0"
                                    aria-hidden="true"
                                />
                                <TruncatedTextTooltip
                                    text={resourceUrl}
                                    className="text-primary small fw-medium"
                                    interactive={false}
                                />
                            </div>
                        </a>
                    )}

                {/* Event Details */}
                {postType === 'event' && eventDate && (
                    <div className="d-flex flex-column gap-2 p-3 mb-3 border rounded bg-light">
                        <div className="d-flex align-items-center gap-2">
                            <i
                                className="bi bi-clock text-primary"
                                aria-hidden="true"
                            />
                            <span>
                                {(() => {
                                    const d = new Date(eventDate);
                                    const mm = String(
                                        d.getMonth() + 1
                                    ).padStart(2, '0');
                                    const dd = String(d.getDate()).padStart(
                                        2,
                                        '0'
                                    );
                                    return `${mm}.${dd}.${d.getFullYear()}`;
                                })()}{' '}
                                at{' '}
                                {new Date(eventDate).toLocaleTimeString(
                                    'en-US',
                                    {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                    }
                                )}
                                {eventEndDate && (
                                    <>
                                        {' \u2014 '}
                                        {new Date(
                                            eventEndDate
                                        ).toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                        })}
                                    </>
                                )}
                            </span>
                        </div>
                        {eventLocation && (
                            <div className="d-flex align-items-center gap-2">
                                <i
                                    className="bi bi-geo-alt text-primary"
                                    aria-hidden="true"
                                />
                                <span>{eventLocation}</span>
                            </div>
                        )}
                        {eventRegistrationUrl &&
                            isSafeUrl(eventRegistrationUrl) && (
                                <div className="d-flex align-items-center gap-2">
                                    <i
                                        className="bi bi-link-45deg text-primary"
                                        aria-hidden="true"
                                    />
                                    <a
                                        href={eventRegistrationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Register: ${eventRegistrationUrl}`}
                                    >
                                        Register
                                    </a>
                                </div>
                            )}
                    </div>
                )}

                {/* Post Media */}
                {mediaUrl && (
                    <div className="post-media mb-3">
                        <Image
                            src={mediaUrl}
                            alt={
                                title
                                    ? `Image for ${title}`
                                    : 'Post media image'
                            }
                            fluid
                            className="w-100 post-media-image rounded"
                        />
                    </div>
                )}

                {/* Post Content */}
                <div className="post-content mb-3">
                    <div
                        id={`post-text-${postId}`}
                        className={`post-text mb-2${isContentCollapsed ? ' post-text-collapsed' : ''}`}
                    >
                        <ReactMarkdown>{content}</ReactMarkdown>
                    </div>
                    {isLongContent && (
                        <Button
                            variant="link"
                            className="p-0 text-dark"
                            onClick={() =>
                                setIsContentExpanded((prev) => !prev)
                            }
                            aria-expanded={isContentExpanded}
                            aria-controls={`post-text-${postId}`}
                        >
                            {isContentExpanded ? 'Show less' : 'Read more'}
                        </Button>
                    )}
                    {contentHighlight && (
                        <p className="post-highlight text-muted fst-italic">
                            {contentHighlight}
                        </p>
                    )}
                </div>

                {flashMessage && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={onDismissFlash}
                        className="mb-3"
                    >
                        {flashMessage}
                    </Alert>
                )}

                {/* Post Actions */}
                <PostActions
                    likes={likes}
                    dislikes={dislikes}
                    comments={localCommentCount}
                    shares={shares}
                    reaction={reaction}
                    isBookmarked={isBookmarked}
                    showingReplies={showReplies}
                    user={user}
                    shareUrl={shareUrl}
                    shareTitle={title}
                    shareCommunityName={communityName}
                    onLike={onLike}
                    onDislike={onDislike}
                    onComment={handleToggleReplies}
                    onBookmark={onBookmark}
                />

                {/* Reply Section - shown when expanded */}
                {showReplies && postId && (
                    <ReplySection
                        postId={postId}
                        isModerator={isModerator}
                        onReplyCountChange={handleReplyCountChange}
                    />
                )}
            </Card.Body>
        </Card>
    );
};

PostCard.propTypes = {
    post: PropTypes.shape({
        postId: PropTypes.string,
        authorUsername: PropTypes.string,
        communityName: PropTypes.string,
        postType: PropTypes.string,
        postIcon: PropTypes.string,
        title: PropTypes.string,
        userName: PropTypes.string,
        userProfilePic: PropTypes.string,
        userDate: PropTypes.string.isRequired,
        userFullDate: PropTypes.string,
        content: PropTypes.string.isRequired,
        contentHighlight: PropTypes.string,
        mediaUrl: PropTypes.string,
        titleAsLink: PropTypes.bool,
        resourceUrl: PropTypes.string,
        resourceType: PropTypes.string,
        eventDate: PropTypes.string,
        eventEndDate: PropTypes.string,
        eventLocation: PropTypes.string,
        eventRegistrationUrl: PropTypes.string,
        likes: PropTypes.number,
        dislikes: PropTypes.number,
        comments: PropTypes.number,
        shares: PropTypes.number,
        isPinned: PropTypes.bool,
        slug: PropTypes.string,
        status: PropTypes.string,
        deletedByRole: PropTypes.oneOf(['author', 'moderator', 'admin']),
        flashMessage: PropTypes.string,
    }).isRequired,
    engagement: PropTypes.shape({
        reaction: PropTypes.oneOf(['like', 'dislike', null]),
        isBookmarked: PropTypes.bool,
    }).isRequired,
    handlers: PropTypes.shape({
        onLike: PropTypes.func.isRequired,
        onDislike: PropTypes.func.isRequired,
        onComment: PropTypes.func,
        onBookmark: PropTypes.func.isRequired,
        onReport: PropTypes.func.isRequired,
        onDelete: PropTypes.func,
        onPin: PropTypes.func,
        onDismissFlash: PropTypes.func,
    }).isRequired,
    permissions: PropTypes.shape({
        isModerator: PropTypes.bool,
        canDelete: PropTypes.bool,
        user: PropTypes.object,
    }).isRequired,
};

export default PostCard;
